import { createAction, createGetter, on, select } from '../../../factories';
import { StoreFeature } from '../../../store';
import { SOFTLINE_STORE_DETAIL_SERVICE_FACTORY } from '../single/entity.shared';
import { Dictionary } from '../../../../types/dictionary';
import * as ActionStore from '../../remote/action.store';
import {
  ActionState,
  handleObservableActionState,
} from '../../remote/action.store';
import { handleSubscriptionState } from '../../remote/subscription.store';
import { SOFTLINE_SERVICE_UUID } from '../../../../core.shared';
import { lastValueFrom } from 'rxjs';
import * as DetailStore from './entity-detail.store';
import { Entity } from '../types/entity';

export interface LoadDetailActionParameters<
  T extends object,
  K extends keyof T & string = keyof T & string
> {
  id: string | number;
  name: K;
  pathParams?: Dictionary<unknown>;
  queryParams?: Dictionary<unknown>;
  token?: string;
}

export interface State<T extends object = object>
  extends DetailStore.State<T> {}

export class Store<TEntity extends Entity, T extends object = object> {
  mutations = {
    ...this.detailStore.mutations,
  };

  actions = {
    loadDetail: createAction<State<T>, LoadDetailActionParameters<T>, unknown>(
      'loadDetail'
    ),
    loadDetailOnce: createAction<
      State<T>,
      LoadDetailActionParameters<T>,
      unknown
    >('loadDetailOnce'),
  };

  getters = {
    ...this.detailStore.getters,
    loadingDetail: createGetter<State<T>, boolean>('loadingDetail'),
    loadedDetail: createGetter<State<T>, boolean>('loadedDetail'),
  };

  feature: StoreFeature<State<T>> = {
    initialState: {
      ...this.detailStore.feature.initialState,
    },
    mutations: [...this.detailStore.feature.mutations],
    getters: [
      ...this.detailStore.feature.getters,
      select(this.getters.loadingDetail, ({ get, featureName, params }) => {
        const actions = [this.actions.loadDetail.name];
        const states: ActionState[] = ['pending', 'processing'];
        return get(featureName, ActionStore.getters.hasState, {
          actions,
          states,
          id: params,
        });
      }),
      select(this.getters.loadedDetail, ({ get, featureName, params }) => {
        const actions = [this.actions.loadDetail.name];
        const states: ActionState[] = ['succeeded', 'failed'];
        return get(featureName, ActionStore.getters.hasState, {
          actions,
          states,
          id: params,
        });
      }),
    ],
    actions: [
      on(
        this.actions.loadDetail,
        async ({ commit, featureName, params, injector }) => {
          const factory = injector.get(SOFTLINE_STORE_DETAIL_SERVICE_FACTORY);
          const service = factory(params.name, injector);
          const token = params.token ?? injector.get(SOFTLINE_SERVICE_UUID)();

          const subscription$ = handleSubscriptionState(
            service.get(params.id, params.pathParams, params.queryParams),
            featureName,
            commit,
            token
          );
          const result = await lastValueFrom(
            handleObservableActionState(
              subscription$,
              featureName,
              commit,
              this.actions.loadDetail.name,
              token
            )
          );
          commit(featureName, this.mutations.setDetail, {
            name: params.name,
            value: result,
          });
          return result;
        }
      ),
      on(
        this.actions.loadDetailOnce,
        async ({ commit, get, dispatch, featureName, params, injector }) => {
          let detail = get(featureName, this.getters.detail(), params.name);
          if (!detail)
            detail = (await dispatch(
              featureName,
              this.actions.loadDetail,
              params,
              injector
            )) as any;
          return detail;
        }
      ),
    ],
  };

  constructor(private detailStore: DetailStore.Store<TEntity, T>) {}
}

export function create<TEntity extends Entity, T extends object>(): Store<
  TEntity,
  T
> {
  const entityStore = DetailStore.create<TEntity, T>();
  return new Store<TEntity, T>(entityStore);
}

const instance = create();
export const mutations = instance.mutations;
export const getters = instance.getters;
export const actions = instance.actions;
export const feature = instance.feature;
